import { Screen } from '@/components/layout'
import { Banner } from '@/components/primitives'
import { MetricRow, RecommendationCard, type MetricSpec } from '@/components/cards'
import { BarChart, type BarDatum } from '@/components/charts/BarChart'
import { List, MonthRow } from '@/components/rows'
import {
  EmptyState,
  ErrorState,
  InsufficientData,
  LoadingRegion,
  SkeletonCard,
  SkeletonList,
  SkeletonMetrics,
} from '@/components/states'
import { api } from '@/lib/api'
import { aggregateUnitState, useProseState } from '@/lib/analysis'
import { t } from '@/lib/copy'
import { formatMonthShort, formatMoney } from '@/lib/format'
import { Link, useNavigate } from '@/lib/router'
import { colorVar } from '@/lib/tokens'
import { useAsync } from '@/lib/useAsync'

/**
 * Money in against money out by month, and how long savings would last.
 *
 * The chart and the metrics are entirely deterministic; the only prose here is
 * on the recommendation cards, so `analysisPending` and `analysisFailed` are
 * silent and render exactly as `ready`.
 */
export function CashFlowScreen() {
  const navigate = useNavigate()
  const cashFlow = useAsync(() => api.analysis.cashFlow(12), [])
  const recommendations = useAsync(() => api.recommendations.list({ theme: 'cashFlow' }), [])

  const data = cashFlow.state.data
  const months = data?.months ?? []
  const complete = months.filter((m) => m.complete !== false)
  const currency = data?.currency ?? 'USD'

  const related = (recommendations.state.data ?? []).slice(0, 2)
  const prose = useProseState(
    aggregateUnitState(related.map((rec) => rec.proseState)),
    recommendations.reload,
  )

  const dataState =
    cashFlow.state.status === 'error'
      ? 'error'
      : cashFlow.state.status === 'loading' && !data
        ? 'loading'
        : months.length === 0
          ? 'empty'
          : complete.length < 2
            ? 'insufficientData'
            : 'ready'

  const screenState = dataState === 'ready' ? (prose.state ?? 'ready') : dataState

  // The three states that render the identical tree below. `analysisPending`
  // adds no spinner and disables nothing; `analysisFailed` is not the error
  // treatment. The only difference between them and `ready` is whether the
  // prose slots hold generated text or their copyKey — and they hold one
  // either way, so the layout does not move when prose arrives.
  const rendersContent =
    screenState === 'ready' || screenState === 'analysisPending' || screenState === 'analysisFailed'

  const bars: BarDatum[] = months.map((month) => ({
    label: formatMonthShort(month.month),
    muted: month.complete === false,
    series: [
      { key: 'in', value: month.income.amount, color: colorVar('moneyIn'), label: t('cashFlow.legend.income') },
      {
        key: 'out',
        value: Math.abs(month.spending.amount),
        color: colorVar('moneyOut'),
        label: t('cashFlow.legend.spending'),
      },
    ],
  }))

  const latest = months.at(-1)
  const metrics: MetricSpec[] = latest
    ? [
        { key: 'income', label: t('cashFlow.metric.income'), value: formatMoney(latest.income, { absolute: true }) },
        { key: 'spending', label: t('cashFlow.metric.spending'), value: formatMoney(latest.spending, { absolute: true }) },
        {
          key: 'net',
          label: t('cashFlow.metric.net'),
          value: formatMoney(latest.net, { signed: true }),
          tone: latest.net.amount >= 0 ? 'positive' : 'negative',
        },
        {
          key: 'average',
          label: t('cashFlow.metric.averageNet'),
          value: formatMoney(data?.averageNet, { signed: true }),
        },
        {
          key: 'runway',
          label: t('cashFlow.metric.runway'),
          value:
            data?.runwayMonths !== null && data?.runwayMonths !== undefined
              ? t('cashFlow.runway.months', { count: Math.round(data.runwayMonths) })
              : '—',
        },
      ]
    : []

  return (
    <Screen
      id="cashFlow"
      state={screenState}
      actions={
        <Link screen="savings" className="btn btn-secondary">
          {t('savings.title')}
        </Link>
      }
    >
      {screenState === 'loading' ? (
        <LoadingRegion>
          <SkeletonCard height="var(--space-xxl)" />
          <SkeletonMetrics count={5} />
          <SkeletonList rows={6} />
        </LoadingRegion>
      ) : screenState === 'error' ? (
        <ErrorState
          titleKey="cashFlow.error.title"
          error={cashFlow.state.status === 'error' ? cashFlow.state.error : undefined}
          onRetry={cashFlow.reload}
        />
      ) : screenState === 'empty' ? (
        <EmptyState
          titleKey="cashFlow.empty.title"
          actionLabel={t('dashboard.cta.upload')}
          onAction={() => navigate('transactions')}
        />
      ) : screenState === 'insufficientData' ? (
        <InsufficientData titleKey="cashFlow.insufficientData.title" bodyKey="cashFlow.insufficientData.body" />
      ) : rendersContent ? (
        <div className="stack">
          {(data?.negativeMonthCount ?? 0) > 0 ? (
            <Banner tone="caution">
              {t('cashFlow.negative', { count: data?.negativeMonthCount ?? 0, total: complete.length })}
            </Banner>
          ) : null}

          <div className="card card-pad-lg stack-sm">
            <BarChart
              data={bars}
              caption={t('cashFlow.title')}
              formatValue={(value) => formatMoney({ amount: value, currency }, { absolute: true })}
            />
            <div className="chart-legend">
              <span className="chart-legend-item">
                <span className="chart-legend-dot" style={{ background: colorVar('moneyIn') }} />
                {t('cashFlow.legend.income')}
              </span>
              <span className="chart-legend-item">
                <span className="chart-legend-dot" style={{ background: colorVar('moneyOut') }} />
                {t('cashFlow.legend.spending')}
              </span>
            </div>
          </div>

          <MetricRow metrics={metrics} />

          {related.map((rec) => (
            <RecommendationCard
              key={rec.id}
              recommendation={rec}
              onSelect={() => navigate('recommendationDetail', { id: rec.id })}
            />
          ))}

          <List>
            {[...months].reverse().map((month) => (
              <MonthRow
                key={month.month}
                month={month.month}
                income={month.income}
                spending={month.spending}
                net={month.net}
                complete={month.complete !== false}
                onSelect={() => navigate('categories')}
              />
            ))}
          </List>
        </div>
      ) : null}
    </Screen>
  )
}
